import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface SpeciesCardProps {
  species: {
    id: number;
    commonName: string;
    scientificName: string;
    category: string;
    image: string;
    alt: string;
    conservationStatus: string;
    sightingProbability: string;
    description: string;
  };
  onViewDetails: (id: number) => void;
}

const SpeciesCard = ({ species, onViewDetails }: SpeciesCardProps) => {
  const getStatusStyle = (status: string) => {
    switch (status.toLowerCase()) {
      case 'least concern':
        return { backgroundColor: 'rgba(34,197,94,0.12)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.25)' };
      case 'near threatened':
        return { backgroundColor: 'rgba(255,138,61,0.12)', color: '#FF8A3D', border: '1px solid rgba(255,138,61,0.25)' };
      case 'vulnerable':
        return { backgroundColor: 'rgba(251,191,36,0.12)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.25)' };
      case 'endangered':
        return { backgroundColor: 'rgba(239,68,68,0.12)', color: '#f87171', border: '1px solid rgba(239,68,68,0.25)' };
      default:
        return { backgroundColor: 'rgba(255,255,255,0.06)', color: '#C8DDE6', border: '1px solid rgba(255,255,255,0.1)' };
    }
  };

  const getProbabilityColor = (probability: string) => {
    switch (probability.toLowerCase()) {
      case 'very high': return '#4ade80';
      case 'high': return '#6FD3E0';
      case 'moderate': return '#FF8A3D';
      default: return '#C8DDE6';
    }
  };

  return (
    <div
      className="rounded-xl overflow-hidden transition-all duration-200 transform hover:-translate-y-1"
      style={{
        background: '#0F2234',
        border: '1px solid rgba(111,211,224,0.12)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.30)',
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLElement;
        el.style.border = '1px solid rgba(111,211,224,0.30)';
        el.style.boxShadow = '0 8px 30px rgba(0,0,0,0.45)';
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLElement;
        el.style.border = '1px solid rgba(111,211,224,0.12)';
        el.style.boxShadow = '0 4px 20px rgba(0,0,0,0.30)';
      }}
    >
      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={species.image}
          alt={species.alt}
          className="w-full h-full object-cover"
        />
        <div
          className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-medium"
          style={{ background: 'rgba(11,22,34,0.80)', color: '#EAF4F8', backdropFilter: 'blur(4px)' }}
        >
          {species.category}
        </div>
      </div>

      <div className="p-5 space-y-3">
        <div>
          <h3 className="text-lg font-headline font-bold mb-1" style={{ color: '#EAF4F8' }}>
            {species.commonName}
          </h3>
          <p className="text-sm italic" style={{ color: '#C8DDE6' }}>
            {species.scientificName}
          </p>
        </div>

        <p className="text-sm line-clamp-2" style={{ color: '#C8DDE6' }}>
          {species.description}
        </p>

        <div className="flex items-center justify-between pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={getStatusStyle(species.conservationStatus)} className="px-3 py-1 rounded-md text-xs font-medium">
            {species.conservationStatus}
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="EyeIcon" size={16} style={{ color: getProbabilityColor(species.sightingProbability) }} />
            <span style={{ color: getProbabilityColor(species.sightingProbability) }} className="text-xs font-medium">
              {species.sightingProbability}
            </span>
          </div>
        </div>

        {/* Primary CTA */}
        <button
          onClick={() => onViewDetails(species.id)}
          className="w-full mt-3 py-2.5 px-4 rounded-full font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2"
          style={{ background: '#6FD3E0', color: '#0B1622', border: '1.5px solid #6FD3E0' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#5ec4d1'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = '#6FD3E0'; }}
        >
          <span>View Details</span>
          <Icon name="ChevronRightIcon" size={16} />
        </button>
      </div>
    </div>
  );
};

export default SpeciesCard;